// ---------------------------------------------------------------------------
// A személynév két részben: vezetéknév és keresztnév (2026-09-27). Zod NÉLKÜL, szándékosan:
// az `AuthContext` minden oldalon betöltődik, és ezt használja a fiók nevének összefűzésére;
// egy zod-import a teljes űrlapcsomagot (zod + react-hook-form, ~26 kB gzip) a nyitóoldalra
// húzná. A mezők szabálya (zod) a `validation/name.ts`-ben él.
//
// Egy „Teljes név” mezőbe sok vendég csak a keresztnevét írta, vagy fordított sorrendben a
// nevét, és ez a számlán, a foglalások listájában és a levelek megszólításában is így maradt.
// Két címkézett mezővel a sorrend nem kérdés, és egyik rész sem maradhat ki.
//
// A tárolt TELJES név (`name` a foglaláson, `displayName` a fiókon) marad, magyar sorrendben
// összefűzve („Kovács Anna”): minden eddigi olvasó (listák, keresés, levelek, a számla vevője, a
// Teya fizetőoldala) változatlanul ezt olvassa. Mellette a két rész külön mezőben is megmarad
// (`lastName` / `firstName`), hogy a következő űrlap ne tippelni kényszerüljön.
// ---------------------------------------------------------------------------

/** A név két része. */
export interface PersonName {
  lastName: string;
  firstName: string;
}

/** Egy névrész leghosszabb értelmes hossza: a kettő szóközzel összefűzve 61 karakter. */
export const NAME_PART_MAX_LENGTH = 30;

export const LAST_NAME_MESSAGE = "Add meg a vezetékneved";
export const FIRST_NAME_MESSAGE = "Add meg a keresztneved";

const collapseSpaces = (value: string | undefined | null): string =>
  (value ?? "").replace(/\s+/g, " ").trim();

/** A teljes név magyar sorrendben: „Kovács” + „Anna” -> „Kovács Anna”. */
export const composeFullName = (lastName: string | undefined, firstName: string | undefined): string =>
  collapseSpaces(`${collapseSpaces(lastName)} ${collapseSpaces(firstName)}`);

/**
 * Egy egy mezőben tárolt név TIPPELT szétbontása, magyar sorrendet feltételezve: az első szó a
 * vezetéknév, a többi a keresztnév. A ponttal végződő előtag (dr., ifj., id., özv.) a vezetéknévvel
 * marad. Egyetlen szó a KERESZTNÉV: a „csak a keresztnevét írta be” a gyakoribb eset.
 *
 * Csak előtöltésre való, a vendég látja és javíthatja: egy fordított sorrendű név (egy
 * Google-fiók „Anna Kovács”-a) így fordítva tippelődik, de a két címkézett mezőben ez szembetűnik.
 */
export const splitFullName = (fullName: string | undefined | null): PersonName => {
  const words = collapseSpaces(fullName).split(" ").filter(Boolean);
  if (words.length === 0) return { lastName: "", firstName: "" };
  if (words.length === 1) return { lastName: "", firstName: words[0] };
  let split = 1;
  while (split < words.length - 1 && words[split - 1].endsWith(".")) split += 1;
  return {
    lastName: words.slice(0, split).join(" "),
    firstName: words.slice(split).join(" "),
  };
};

/**
 * A fiók nevének két része: a külön mentett rész, ha az még a fiók MOSTANI teljes nevét adja ki,
 * különben a teljes név tippelt bontása. Az egyezés azért kell, mert a teljes nevet más is
 * átírhatja (az admin a felhasználók listájában egy mezőben): egy elavult rész nem győzhet.
 */
export const resolvePersonName = (
  /** Egy foglalás vagy egy `users` dokumentum: a `lastName` / `firstName` mezőit olvassuk */
  saved: object | null | undefined,
  fullName: string | undefined | null,
): PersonName => {
  const record = (saved ?? {}) as { lastName?: unknown; firstName?: unknown };
  const lastName = typeof record.lastName === "string" ? collapseSpaces(record.lastName) : "";
  const firstName = typeof record.firstName === "string" ? collapseSpaces(record.firstName) : "";
  if (lastName && firstName && composeFullName(lastName, firstName) === collapseSpaces(fullName)) {
    return { lastName, firstName };
  }
  return splitFullName(fullName);
};
